import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { AiFillClockCircle } from "react-icons/ai";
import { FaLock } from "react-icons/fa";
import DataContext from "../contexts/DataProvider";
import FormDataContext from "../contexts/FormData";
import Countdown from "react-countdown";
import FormatAmount from "../helper/FormatAmount";
import pci from "../assets/pci.png";
import swinkpay_logo from "../assets/swinklogo.png";
import SwinkSpinner from "./SwinkSpinner";
import PaymentHeader from "./PaymentHeader";

const ProgressScreen = ({ setShowProcessingScreen, setShowModal, showModal, timeLeft }) => {
  const { data } = useContext(DataContext);
  const { formData } = useContext(FormDataContext);
  const [percentageLeft, setPercentageLeft] = useState("0%");

  const isDeepLink = formData?.modeOfPayment === "QRCODE";

  const steps = isDeepLink
    ? [
        { id: 1, item: "Enter your UPI PIN in the app" },
        { id: 2, item: "Confirm the payment amount" },
        { id: 3, item: "Return here once payment is done" },
      ]
    : [
        { id: 1, item: "Open your UPI app on your phone" },
        { id: 2, item: `Select the payment request from ${data?.merchantName || "the merchant"}` },
        { id: 3, item: "Enter your UPI PIN to complete the payment" },
      ];

  const getDynamicPer = (time, completed) => {
    if (completed) {
      setPercentageLeft("100%");
    } else {
      const totalDuration = 5 * 60;
      const perLeft = (time / totalDuration) * 100;
      setPercentageLeft(`${100 - perLeft}%`);
    }
  };

  const renderer = ({ hours, minutes, seconds, completed }) => {
    const currentTime = minutes * 60 + seconds;
    getDynamicPer(currentTime, completed);
    const newSeconds = Number(seconds) < 10 ? "0" + seconds : seconds;
    const totalMins = hours * 60 + minutes + seconds / 60;

    if (completed) {
      setShowModal(true);
      setShowProcessingScreen(true);
      return null;
    }
    // Light-theme pill for the white header.
    const color = totalMins >= 1 ? "#1E56C8" : "#DC2626";
    const borderColor = totalMins >= 2 ? "rgba(30,86,200,0.30)" : totalMins >= 1 ? "rgba(245,158,11,0.7)" : "rgba(220,38,38,0.7)";
    return (
      <div className="flex items-center gap-1 px-2 py-0.5 rounded-full transition-all duration-700"
           style={{ background: "rgba(30,86,200,0.06)", border: `1px solid ${borderColor}` }}>
        <AiFillClockCircle style={{ color, fontSize: 10 }} />
        <span className="text-xs font-bold tabular-nums" style={{ color }}>{`0${minutes}:${newSeconds}`}</span>
        <span className="text-[10px] font-medium text-gray-400">mins</span>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">

      {/* ── Header (shared white header) ── */}
      <div className="flex-none w-full" style={{ height: 112 }}>
        <PaymentHeader
          amountLabel="PAYABLE AMOUNT"
          labelColor="#1E56C8"
          right={!showModal ? <Countdown date={timeLeft} renderer={renderer} /> : null}
        />
      </div>

      {/* ── Body ── */}
      <div className="flex-grow flex flex-col px-4 pt-6 pb-4 bg-gray-50 overflow-hidden justify-start">

        {/* Processing animation + status */}
        <div className="flex flex-col items-center mb-5">
          <div className="mb-4">
            <SwinkSpinner size={92} />
          </div>
          <p className="text-sm font-bold text-gray-800">
            {isDeepLink ? "Payment in Progress" : "Payment Request Sent!"}
          </p>
          <p className="text-xs text-gray-400 mt-0.5 text-center">
            {isDeepLink ? "Complete payment in your UPI app" : "Waiting for you to approve in your UPI app"}
          </p>

          {/* Progress bar */}
          <div className="w-full mt-3 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-1000"
                 style={{ width: percentageLeft, background: "linear-gradient(90deg, #1E56C8, #1340A0)" }} />
          </div>
        </div>

        {/* Steps card — animated timeline */}
        <div className="rounded-2xl mb-4"
             style={{ background: "white", border: "1px solid rgba(30,86,200,0.1)", boxShadow: "0 2px 16px rgba(30,86,200,0.08)" }}>
          <div className="px-4 pt-3 pb-2 flex items-center gap-2">
            <div className="w-1 h-4 rounded-full" style={{ background: "linear-gradient(180deg,#1E56C8,#1340A0)" }} />
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Steps to complete</p>
          </div>
          <div className="px-4 pb-3 relative">
            {/* Vertical track */}
            <div className="absolute" style={{ left: 27, top: 4, bottom: 16, width: 2,
              background: "linear-gradient(180deg,#1E56C8 0%,rgba(30,86,200,0.15) 100%)", borderRadius: 2 }} />
            {steps.map((step, i) => (
              <div key={step.id} className="flex items-start gap-3 relative"
                   style={{ marginBottom: i < steps.length - 1 ? 20 : 0 }}>
                {/* Step node */}
                <div className="relative z-10 flex-shrink-0" style={{ width: 28 }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                       style={i === 0 ? {
                         background: "linear-gradient(135deg,#1E56C8,#1340A0)", color: "white",
                         boxShadow: "0 0 0 4px rgba(30,86,200,0.15), 0 2px 8px rgba(30,86,200,0.3)"
                       } : {
                         background: "white", color: "#9CA3AF", border: "2px solid #E5E7EB"
                       }}>
                    {step.id}
                  </div>
                  {i === 0 && (
                    <div className="absolute inset-0 rounded-full"
                         style={{ background: "rgba(30,86,200,0.22)", animation: "ring-pulse 1.8s ease-out infinite" }} />
                  )}
                </div>
                {/* Step text */}
                <div className="pt-0.5 flex-1">
                  <p className={`text-sm leading-snug ${i === 0 ? "font-semibold text-gray-800" : "font-medium text-gray-400"}`}>
                    {step.item}
                  </p>
                  {i === 0 && (
                    <div className="mt-1 flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#1E56C8" }} />
                      <span className="text-[10px] font-semibold text-[#1E56C8]">
                        {isDeepLink ? "Awaiting payment confirmation…" : "Waiting for approval…"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Warning banner */}
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
             style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.25)" }}>
          <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none">
            <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  stroke="#FBBF24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="text-xs font-medium text-amber-700">Do not press back or close this window</p>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="flex-none bg-white border-t border-gray-100">
        <div className="flex items-center justify-center gap-2 py-1.5 px-3">
          <FaLock className="text-[#1E56C8] text-xs" />
          <span className="text-[10px] text-gray-400 font-medium">256-bit Encrypted</span>
          <span className="text-gray-200 text-xs">|</span>
          <img src={pci} alt="pci_logo" className="h-5 w-auto" />
          <span className="text-gray-200 text-xs">|</span>
          <img src={swinkpay_logo} alt="swinkpay_logo" className="h-5 w-auto" />
        </div>
      </div>
    </div>
  );
};

export default ProgressScreen;

ProgressScreen.propTypes = {
  setShowProcessingScreen: PropTypes.func,
  setShowModal: PropTypes.func,
  showModal: PropTypes.bool,
  timeLeft: PropTypes.string,
};
